import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  MenuItem, Divider, Stack, Tooltip, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';

const emptyField = () => ({
  key: '',
  label: '',
  required: true,
  validation: {
    type: 'text',
    pattern: '',
    maxLength: '',
    uppercase: false,
    digitsOnly: false,
    options: []
  }
});

function FieldDialog({ open, onClose, initial, onSave, existingKeys }) {
  const { t } = useTranslation();
  const [field, setField] = useState(initial || emptyField());
  const [optionInput, setOptionInput] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setField(initial || emptyField());
    setErrors({});
    setOptionInput('');
  }, [initial, open]);

  const setVal = (path, value) => {
    setField(prev => {
      const next = { ...prev };
      if (path === 'key') next.key = value;
      else if (path === 'label') next.label = value;
      else if (path === 'required') next.required = value;
      else if (path.startsWith('validation.')) {
        const vKey = path.replace('validation.', '');
        next.validation = { ...next.validation, [vKey]: value };
        // reset text-only or select-only sub-fields when type changes
        if (vKey === 'type') {
          if (value === 'select') {
            next.validation = { type: 'select', options: next.validation.options || [] };
          } else {
            next.validation = {
              type: value,
              pattern: '',
              maxLength: '',
              uppercase: false,
              digitsOnly: false
            };
          }
        }
      }
      return next;
    });
  };

  const handleAddOption = () => {
    const trimmed = optionInput.trim();
    if (!trimmed) return;
    setField(prev => ({
      ...prev,
      validation: {
        ...prev.validation,
        options: [...(prev.validation.options || []), trimmed]
      }
    }));
    setOptionInput('');
  };

  const handleRemoveOption = (idx) => {
    setField(prev => ({
      ...prev,
      validation: {
        ...prev.validation,
        options: prev.validation.options.filter((_, i) => i !== idx)
      }
    }));
  };

  const validate = () => {
    const errs = {};
    if (!field.key.trim()) errs.key = t('pinfo_key_required');
    else if (!/^[a-z0-9_]+$/.test(field.key)) errs.key = t('pinfo_key_invalid');
    else if (existingKeys.includes(field.key) && field.key !== initial?.key) errs.key = t('pinfo_key_exists');
    if (!field.label.trim()) errs.label = t('pinfo_label_required');
    if (field.validation.type === 'select' && (!field.validation.options || field.validation.options.length === 0)) {
      errs.options = t('pinfo_options_required');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    // Clean up: remove empty strings from validation
    const v = { ...field.validation };
    if (v.type === 'text') {
      if (!v.pattern) delete v.pattern;
      if (!v.maxLength) delete v.maxLength;
      else v.maxLength = parseInt(v.maxLength, 10);
      if (!v.uppercase) delete v.uppercase;
      if (!v.digitsOnly) delete v.digitsOnly;
    }
    onSave({ ...field, validation: v });
  };

  const isSelect = field.validation.type === 'select';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial?.key ? t('pinfo_edit_field') : t('pinfo_add_field')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('pinfo_key_label')}
            value={field.key}
            onChange={e => setVal('key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            error={!!errors.key}
            helperText={errors.key || t('pinfo_key_helper')}
            fullWidth
            disabled={!!initial?.key} // prevent renaming existing keys
          />
          <TextField
            label={t('pinfo_label_label')}
            value={field.label}
            onChange={e => setVal('label', e.target.value)}
            error={!!errors.label}
            helperText={errors.label || t('pinfo_label_helper')}
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={!!field.required} onChange={e => setVal('required', e.target.checked)} />}
            label={t('pinfo_required_label')}
          />
          <Divider />
          <TextField
            select
            label={t('pinfo_field_type')}
            value={field.validation.type}
            onChange={e => setVal('validation.type', e.target.value)}
            fullWidth
          >
            <MenuItem value="text">{t('pinfo_type_text')}</MenuItem>
            <MenuItem value="select">{t('pinfo_type_select')}</MenuItem>
          </TextField>

          {!isSelect && (
            <>
              <TextField
                label={t('pinfo_validation_pattern')}
                value={field.validation.pattern || ''}
                onChange={e => setVal('validation.pattern', e.target.value)}
                fullWidth
                placeholder="e.g. ^[A-Za-z0-9]{18}$"
                helperText={t('pinfo_validation_pattern_helper')}
              />
              <TextField
                label={t('pinfo_max_length')}
                type="number"
                value={field.validation.maxLength || ''}
                onChange={e => setVal('validation.maxLength', e.target.value)}
                sx={{ width: '50%' }}
                inputProps={{ min: 1 }}
              />
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={!!field.validation.uppercase} onChange={e => setVal('validation.uppercase', e.target.checked)} />}
                  label={t('pinfo_auto_uppercase')}
                />
                <FormControlLabel
                  control={<Switch checked={!!field.validation.digitsOnly} onChange={e => setVal('validation.digitsOnly', e.target.checked)} />}
                  label={t('pinfo_digits_only')}
                />
              </Stack>
            </>
          )}

          {isSelect && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>{t('pinfo_options')}</Typography>
              {errors.options && <Typography variant="caption" color="error">{errors.options}</Typography>}
              <Stack spacing={1} sx={{ mb: 1 }}>
                {(field.validation.options || []).map((opt, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>{opt}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleRemoveOption(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label={t('pinfo_add_option_key')}
                  value={optionInput}
                  onChange={e => setOptionInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } }}
                  placeholder="e.g. degree_college"
                  sx={{ flex: 1 }}
                  helperText={t('pinfo_option_key_helper')}
                />
                <Button variant="outlined" onClick={handleAddOption} sx={{ alignSelf: 'flex-start', mt: '2px' }}>{t('add')}</Button>
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button variant="contained" onClick={handleSave}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PersonalInfoFieldsSettings() {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null); // null = add, object = edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Drag state
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-personalinfo`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(data => { setFields(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const saveToServer = (updatedFields) => {
    setSaving(true);
    fetch(`${API_ENDPOINTS.BASEURL}/api/required-personalinfo`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ fields: updatedFields })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('settings_saved'), severity: 'success' });
        setSaving(false);
      })
      .catch(() => {
        setSnackbar({ open: true, message: t('settings_save_error'), severity: 'error' });
        setSaving(false);
      });
  };

  const handleSaveField = (fieldData) => {
    let updated;
    if (editingField) {
      // replace existing
      updated = fields.map(f => f.key === editingField.key ? fieldData : f);
    } else {
      updated = [...fields, fieldData];
    }
    setFields(updated);
    saveToServer(updated);
    setDialogOpen(false);
    setEditingField(null);
  };

  const handleDeleteConfirm = () => {
    const updated = fields.filter(f => f.key !== deleteTarget.key);
    setFields(updated);
    saveToServer(updated);
    setDeleteTarget(null);
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const reordered = [...fields];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    setFields(reordered);
    setDragIdx(idx);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    saveToServer(fields);
  };

  const existingKeys = fields.map(f => f.key);

  if (loading) return <Typography variant="body2" color="text.secondary">{t('loading_text')}</Typography>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('pinfo_drag_subtitle')}
      </Typography>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {fields.map((field, idx) => (
          <Paper
            key={field.key}
            variant="outlined"
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'grab',
              opacity: dragIdx === idx ? 0.5 : 1,
              bgcolor: dragIdx === idx ? 'action.hover' : 'background.paper'
            }}
          >
            <Tooltip title={t('drag_to_reorder')}>
              <DragIndicatorIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
            </Tooltip>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{field.label}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>({field.key})</Typography>
                {field.required && <Chip label={t('required_chip')} size="small" color="primary" variant="outlined" />}
                <Chip
                  label={field.validation?.type === 'select' ? t('type_select_chip') : t('type_text_chip')}
                  size="small"
                  variant="outlined"
                />
                {field.validation?.type === 'text' && field.validation?.maxLength && (
                  <Typography variant="caption" color="text.secondary">{t('max_length_display', { n: field.validation.maxLength })}</Typography>
                )}
                {field.validation?.type === 'select' && (
                  <Typography variant="caption" color="text.secondary">{t('options_count', { n: (field.validation.options || []).length })}</Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title={t('edit')}>
                <IconButton size="small" onClick={() => { setEditingField(field); setDialogOpen(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('delete')}>
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(field)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ))}
      </Stack>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => { setEditingField(null); setDialogOpen(true); }}
        disabled={saving}
      >
        {t('pinfo_add_field')}
      </Button>

      <FieldDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingField(null); }}
        initial={editingField}
        onSave={handleSaveField}
        existingKeys={existingKeys}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('pinfo_delete_title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('pinfo_delete_prompt', { label: deleteTarget?.label, key: deleteTarget?.key })}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {t('pinfo_delete_warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
